







// src/pages/PrePrimary.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import LearningItemCard from "../components/LearningItemCard";
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";

export default function PrePrimary() {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 🔹 lower-case values
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fav_preprimary") || "[]");
    } catch {
      return [];
    }
  });

  // Slider state
  const [alphaIndex, setAlphaIndex] = useState(0);
  const [numIndex, setNumIndex] = useState(0);

  // A–Z data
  const alphabetSlides = [
    { letter: "A", word: "Apple", emoji: "🍎", sentence: "A is for Apple, a red and juicy fruit." },
    { letter: "B", word: "Ball", emoji: "🏀", sentence: "B is for Ball, we love to play with it." },
    { letter: "C", word: "Cat", emoji: "🐱", sentence: "C is for Cat, a cute and soft pet." },
    { letter: "D", word: "Dog", emoji: "🐶", sentence: "D is for Dog, a loyal friend." },
    { letter: "E", word: "Egg", emoji: "🥚", sentence: "E is for Egg, small and oval." },
    { letter: "F", word: "Fish", emoji: "🐟", sentence: "F is for Fish, it swims in water." },
    { letter: "G", word: "Goat", emoji: "🐐", sentence: "G is for Goat, it gives us milk." },
    { letter: "H", word: "Hat", emoji: "👒", sentence: "H is for Hat, we wear it on sunny days." },
    { letter: "I", word: "Ice-cream", emoji: "🍦", sentence: "I is for Ice-cream, a cold sweet treat." },
    { letter: "J", word: "Jug", emoji: "🏺", sentence: "J is for Jug, it holds water or juice." },
    { letter: "K", word: "Kite", emoji: "🪁", sentence: "K is for Kite, it flies high in the sky." },
    { letter: "L", word: "Lion", emoji: "🦁", sentence: "L is for Lion, the king of the jungle." },
    { letter: "M", word: "Mango", emoji: "🥭", sentence: "M is for Mango, a tasty fruit." },
    { letter: "N", word: "Nest", emoji: "🪹", sentence: "N is for Nest, where birds live." },
    { letter: "O", word: "Orange", emoji: "🍊", sentence: "O is for Orange, round and juicy." },
    { letter: "P", word: "Parrot", emoji: "🦜", sentence: "P is for Parrot, a colorful talking bird." },
    { letter: "Q", word: "Queen", emoji: "👸", sentence: "Q is for Queen, she wears a crown." },
    { letter: "R", word: "Rabbit", emoji: "🐰", sentence: "R is for Rabbit, it hops happily." },
    { letter: "S", word: "Sun", emoji: "☀️", sentence: "S is for Sun, shining bright in the sky." },
    { letter: "T", word: "Tiger", emoji: "🐯", sentence: "T is for Tiger, strong and brave." },
    { letter: "U", word: "Umbrella", emoji: "☔", sentence: "U is for Umbrella, we use it in the rain." },
    { letter: "V", word: "Van", emoji: "🚐", sentence: "V is for Van, it carries people and things." },
    { letter: "W", word: "Watch", emoji: "⌚", sentence: "W is for Watch, it tells us the time." },
    { letter: "X", word: "Xylophone", emoji: "🎼", sentence: "X is for Xylophone, a musical instrument." },
    { letter: "Y", word: "Yak", emoji: "🐃", sentence: "Y is for Yak, a strong mountain animal." },
    { letter: "Z", word: "Zebra", emoji: "🦓", sentence: "Z is for Zebra, with black and white stripes." },
  ];

  // 1–10 data
  const numberSlides = [
    { num: 1, text: "1 teddy bear 🧸" },
    { num: 2, text: "2 cars 🚗🚙" },
    { num: 3, text: "3 blocks 🧱🧱🧱" },
    { num: 4, text: "4 balls ⚽⚽⚽⚽" },
    { num: 5, text: "5 dolls 🪆🪆🪆🪆🪆" },
    { num: 6, text: "6 crayons 🖍️🖍️🖍️🖍️🖍️🖍️" },
    { num: 7, text: "7 toy animals 🐶🐱🐰🐯🐷🐭🐻" },
    { num: 8, text: "8 rings 🔵🔵🔵🔵🔵🔵🔵🔵" },
    { num: 9, text: "9 toy cars 🚙🚙🚙🚙🚙🚙🚙🚙🚙" },
    { num: 10, text: "10 blocks 🧱🧱🧱🧱🧱🧱🧱🧱🧱🧱" },
  ];

  const nextAlpha = () =>
    setAlphaIndex((prev) => (prev + 1) % alphabetSlides.length);
  const prevAlpha = () =>
    setAlphaIndex((prev) => (prev - 1 + alphabetSlides.length) % alphabetSlides.length);

  const nextNum = () =>
    setNumIndex((prev) => (prev + 1) % numberSlides.length);
  const prevNum = () =>
    setNumIndex((prev) => (prev - 1 + numberSlides.length) % numberSlides.length);

  useEffect(() => {
    localStorage.setItem("fav_preprimary", JSON.stringify(favorites));
  }, [favorites]);

  // Items (text kept mostly for search)
  const items = [
    {
      id: "pp-alpha",
      title: "Alphabets & Phonics",
      description: "Learn letters A–Z with sounds and simple examples.",
      text: "Tap the arrows above to move from A to Z and see one picture and sentence for each letter.",
      video: "https://youtube.com/shorts/aAwbNJpBbUo?si=smJ6CBgb7DvBChbn",
      audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
    },
    {
      id: "pp-count",
      title: "Counting 1–10",
      description: "Counting objects and fingers using songs.",
      text: "Use the number slider above to count 1 to 10 with matching objects.",
      video: "https://youtube.com/shorts/-wgHi_h8K1s?si=G6r8uioW1n3Yqedg",
      audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
    },
    {
      id: "pp-rhyme",
      title: "Nursery Rhymes",
      description: "Short rhymes and actions for memory & language.",
      text: "Sing-along rhymes: clap, stomp, and repeat short phrases to learn rhythm.",
      video: "https://youtu.be/R2frjzrC5Jg?si=eKfBoyJownFfElXv",
      audio: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
    },
  ];

  // 🔹 Search + filter (All / Text / Video / Audio)
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items.filter((it) => {
      const title = (it.title || "").toLowerCase();
      const description = (it.description || "").toLowerCase();
      const text = (it.text || "").toLowerCase();

      const hasText = !!(it.text && it.text.trim().length > 0);
      const hasVideo = !!(it.video && String(it.video).trim().length > 0);
      const hasAudio = !!(it.audio && String(it.audio).trim().length > 0);

      const matchesSearch =
        !q ||
        title.includes(q) ||
        description.includes(q) ||
        text.includes(q);

      let matchesFilter = true;
      if (filter === "text") matchesFilter = hasText;
      if (filter === "video") matchesFilter = hasVideo;
      if (filter === "audio") matchesFilter = hasAudio;

      return matchesSearch && matchesFilter;
    });
  }, [items, search, filter]);

  function toggleFav(id) {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  }

  // Small components used as customContent inside cards
  const AlphabetSlider = () => (
    <div>
      <h4 className="font-semibold mb-2">A–Z with pictures</h4>
      <div className="flex items-center gap-3 bg-yellow-50 rounded-2xl px-4 py-3">
        <button
          onClick={prevAlpha}
          className="p-2 rounded-full bg-white shadow hover:scale-105 transition"
        >
          <FaChevronLeft />
        </button>

        <div className="flex-1 flex items-center gap-4">
          <div className="text-4xl font-extrabold w-14 text-center">
            {alphabetSlides[alphaIndex].letter}
          </div>
          <div className="text-4xl w-14 text-center">
            {alphabetSlides[alphaIndex].emoji}
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold">
              {alphabetSlides[alphaIndex].letter} is for{" "}
              {alphabetSlides[alphaIndex].word}
            </div>
            <div className="text-xs text-gray-700 mt-1">
              {alphabetSlides[alphaIndex].sentence}
            </div>
          </div>
        </div>

        <button
          onClick={nextAlpha}
          className="p-2 rounded-full bg-white shadow hover:scale-105 transition"
        >
          <FaChevronRight />
        </button>
      </div>
      <p className="text-[11px] text-center text-gray-500 mt-1">
        {alphaIndex + 1} / {alphabetSlides.length}
      </p>
    </div>
  );

  const NumberSlider = () => (
    <div>
      <h4 className="font-semibold mb-2">Counting 1–10</h4>
      <div className="flex items-center gap-3 bg-yellow-50 rounded-2xl px-4 py-3">
        <button
          onClick={prevNum}
          className="p-2 rounded-full bg-white shadow hover:scale-105 transition"
        >
          <FaChevronLeft />
        </button>

        <div className="flex-1 flex items-center gap-4">
          <div className="text-4xl font-extrabold w-14 text-center">
            {numberSlides[numIndex].num}
          </div>
          <div className="flex-1 text-base">
            {numberSlides[numIndex].text}
          </div>
        </div>

        <button
          onClick={nextNum}
          className="p-2 rounded-full bg-white shadow hover:scale-105 transition"
        >
          <FaChevronRight />
        </button>
      </div>
      <p className="text-[11px] text-center text-gray-500 mt-1">
        {numIndex + 1} / {numberSlides.length}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-yellow-50 to-yellow-100">
      <div className="max-w-5xl mx-auto">
        {/* Nav */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/elearning")}
            className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded shadow"
          >
            <FaArrowLeft /> Back
          </button>
          <h1 className="text-3xl font-bold">Pre-Primary</h1>
          <div />
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search lessons (title, description, text)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border bg-white shadow"
          />

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 rounded-lg border bg-white shadow md:w-48"
          >
            <option value="all">All</option>
            <option value="text">Text</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
          </select>
        </div>

        {/* Cards */}
        <div className="grid gap-6">
          {filteredItems.map((it) => (
            <LearningItemCard
              key={it.id}
              item={it}
              isFav={favorites.includes(it.id)}
              toggleFav={toggleFav}
              customContent={
                it.id === "pp-alpha"
                  ? <AlphabetSlider />
                  : it.id === "pp-count"
                  ? <NumberSlider />
                  : null
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
